import type { ApiClient } from '../apiClient';

/**
 * Executors — the sequential named A → B release authority.
 *
 * Distinct from guardians: an executor is a SINGLE trusted person who can
 * authorize the release of your vault after end-of-life — not a vote. You name
 * a primary (A) and, optionally, a backup (B) who steps in only if the primary
 * cannot act. An executor holds NO key material; on release, the server delivers
 * each message to the person it was written for. The executor never reads them.
 */

export type ExecutorOrder = 1 | 2;
export type ExecutorStatus = 'invited' | 'active' | 'declined' | 'replaced';

export interface Executor {
  id: string;
  vault_id: string;
  order_index: ExecutorOrder;
  executor_name: string;
  executor_email: string;
  status: ExecutorStatus;
  invitation_expires_at: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
}

export interface ExecutorList {
  executors: Executor[];
}

export interface CreateExecutorInput {
  order_index: ExecutorOrder;
  executor_name: string;
  executor_email: string;
}

export function listExecutors(api: ApiClient, vaultId: string): Promise<ExecutorList> {
  return api.get<ExecutorList>(`/api/vaults/${vaultId}/executors`);
}

export function createExecutor(
  api: ApiClient,
  vaultId: string,
  input: CreateExecutorInput,
): Promise<{ executor: Executor }> {
  return api.post<{ executor: Executor }>(`/api/vaults/${vaultId}/executors`, input);
}

export function inviteExecutor(
  api: ApiClient,
  vaultId: string,
  executorId: string,
): Promise<unknown> {
  return api.post(`/api/vaults/${vaultId}/executors/${executorId}/invite`, {});
}

export function removeExecutor(
  api: ApiClient,
  vaultId: string,
  executorId: string,
): Promise<unknown> {
  return api.del(`/api/vaults/${vaultId}/executors/${executorId}`);
}

export const ORDER_COPY: Record<ExecutorOrder, { label: string; blurb: string }> = {
  1: {
    label: 'Primary executor',
    blurb:
      'The one person you trust to act when you are gone. They alone can authorize the release of your vault.',
  },
  2: {
    label: 'Backup executor',
    blurb:
      'Steps in only if your primary cannot act — never while the primary is able. A second name, in case.',
  },
};
